import { OrderStatusBadge } from "@/components/commerce/commerce-status-badge";
import { CommerceEmptyState } from "@/components/commerce/empty-state";
import { KanbanBoard } from "@/components/kanban/KanbanBoard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { type StatusBarItem, useWorkspacePage } from "@/components/workspace";
import { ExportGuide } from "@/features/onboarding";
import type { SalesOrder } from "@/gen/omnios/v1/order_pb";
import { useCreateOrder, useDeleteOrders } from "@/hooks/use-commerce";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { orderClient } from "@/lib/commerce-client";
import { formatCurrency, formatRelative } from "@/lib/format";
import { useGridLayout } from "@/lib/use-grid-layout";
import { useServerGrid } from "@/lib/use-server-grid";
import { useSlashFocus } from "@/lib/use-slash-focus";
import { cn } from "@/lib/utils";

import type { ColDef, ValueFormatterParams } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { useGridSelection } from "@/hooks/use-grid-selection";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { createFileRoute } from "@tanstack/react-router";
import { Columns3, LayoutList, Plus, Search } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/commerce/orders/")({
	component: OrdersPage,
});

function OrdersPage() {
	const { t } = useTranslation("orders");
	const navigate = useNavigate();
	const qc = useQueryClient();
	const { onGridReady, onStateChanged } = useGridLayout("commerce-orders");

	const searchParams = useSearch({ from: "/commerce/orders/" });
	const viewParam =
		(searchParams as Record<string, unknown>).view === "board"
			? "board"
			: "table";

	const [search, setSearch] = useState("");
	const searchRef = useRef<HTMLInputElement>(null);
	const clearSearch = useCallback(() => setSearch(""), []);
	useSlashFocus(searchRef, clearSearch);

	const { gridRef, gridProps, totalRows, refreshGrid } =
		useServerGrid<SalesOrder>({
			fetchRows: async ({ pageSize, pageToken, sort, filter, search: s }) => {
				const resp = await orderClient.listOrders({
					search: s,
					sort: sort
						? { field: sort.field, descending: sort.descending }
						: undefined,
					filter: filter as any,
					page: { pageSize, pageToken },
				});
				return {
					rows: resp.orders ?? [],
					nextPageToken: resp.page?.nextPageToken,
					totalCount: resp.page?.totalCount ?? 0,
				};
			},
			pageSize: 100,
			search,
			colToField: {
				seqNo: "seq_no",
				customerPartyId: "customer_party_id",
				statusDisplayName: "status_display_name",
				createdAt: "created_at",
				updatedAt: "updated_at",
			},
		});

	const createOrderMut = useCreateOrder();
	const deleteMut = useDeleteOrders();

	const { selectedRows, hasSelection, onSelectionChanged, ActionBar } =
		useGridSelection<SalesOrder>({
			onDelete: (rows) =>
				deleteMut.mutate(
					{ ids: rows.map((r) => r.id), all: false },
					{
						onSuccess: () => {
							refreshGrid();
							toast.success(t("delete_success"));
						},
						onError: () => toast.error(t("delete_error")),
					},
				),
			isPending: deleteMut.isPending,
			entityLabel: t("total_count_label", { defaultValue: "заказов" }),
		});

	// Fetch status options for kanban columns
	const { data: statusOptionsData } = useQuery({
		queryKey: ["commerce", "order-status-options-all"],
		queryFn: () => orderClient.listOrderStatusOptions({}),
	});
	const statusOptions = statusOptionsData?.options ?? [];

	// Board view needs client-side data
	const { data: boardData } = useQuery({
		queryKey: ["commerce", "orders-board", search],
		queryFn: () =>
			orderClient.listOrders({
				search,
				page: { pageSize: 200, pageToken: "" },
			}),
		enabled: viewParam === "board",
	});
	const boardItems = boardData?.orders ?? [];

	const updateOrderStatusMut = useMutation({
		mutationFn: (opts: { id: string; newStatusConfigId: string }) =>
			orderClient.transitionOrderStatus({
				id: opts.id,
				newStatusConfigId: opts.newStatusConfigId,
			}),
		onSuccess: () => {
			refreshGrid();
			qc.invalidateQueries({ queryKey: ["commerce", "orders-board"] });
		},
	});

	// ── Column definitions — React component renderers, no innerHTML ──
	const columnDefs = useMemo<ColDef<SalesOrder>[]>(
		() => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 36,
				maxWidth: 36,
				pinned: "left",
				suppressHeaderMenuButton: true,
				resizable: false,
			},
			{
				field: "seqNo",
				headerName: t("col_number"),
				width: 90,
				pinned: "left",
				cellClass: "font-mono",
				valueFormatter: (p: ValueFormatterParams<SalesOrder>) =>
					p.data?.seqNo != null ? p.data.seqNo.toString() : "",
			},
			{
				field: "title",
				headerName: t("col_name"),
				flex: 2,
				minWidth: 150,
			},
			{
				field: "customerPartyId",
				headerName: t("col_customer"),
				width: 140,
				valueFormatter: (p: ValueFormatterParams<SalesOrder>) =>
					p.data?.customerPartyId ? p.data.customerPartyId.slice(0, 8) : "—",
			},
			{
				field: "statusDisplayName",
				headerName: t("col_status"),
				width: 130,
				filter: false,
				cellRenderer: (p: { data: SalesOrder | undefined }) => {
					if (!p.data) return null;
					return <OrderStatusBadge order={p.data} />;
				},
			},
			{
				headerName: t("col_amount"),
				width: 120,
				type: "rightAligned",
				cellClass: "font-tabular",
				filter: false,
				sortable: false,
				valueGetter: (p) => {
					const lines = p.data?.lines ?? [];
					return lines.reduce(
						(sum, l) =>
							sum +
							Number(l.quantity) *
								Number(l.unitPriceCents) *
								(1 - (l.discountPct ?? 0) / 100),
						0,
					);
				},
				valueFormatter: (p: ValueFormatterParams) =>
					p.value ? formatCurrency(p.value) : "—",
			},
			{
				headerName: t("col_lines"),
				width: 80,
				type: "rightAligned",
				cellClass: "font-tabular",
				filter: false,
				valueGetter: (p) => p.data?.lines?.length ?? 0,
			},
			{
				field: "createdAt",
				headerName: t("col_created"),
				width: 140,
				filter: "agDateColumnFilter",
				valueFormatter: (p: ValueFormatterParams<SalesOrder>) => {
					const ts = p.data?.createdAt;
					return ts ? formatRelative(new Date(Number(ts.seconds) * 1000)) : "—";
				},
			},
			{
				field: "updatedAt",
				headerName: t("col_updated", { defaultValue: "Обновлён" }),
				width: 140,
				filter: "agDateColumnFilter",
				valueFormatter: (p: ValueFormatterParams<SalesOrder>) => {
					const ts = p.data?.updatedAt;
					return ts ? formatRelative(new Date(Number(ts.seconds) * 1000)) : "—";
				},
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			suppressHeaderMenuButton: false,
			filter: true,
			filterParams: { buttons: ["apply", "reset"], closeOnApply: true },
		}),
		[],
	);

	const onRowClicked = useCallback(
		(e: { data: SalesOrder | undefined }) => {
			if (e.data?.id) {
				navigate({
					to: "/commerce/orders/$id",
					params: { id: e.data.id },
				});
			}
		},
		[navigate],
	);

	const handleCreateOrder = useCallback(() => {
		createOrderMut.mutate(
			{
				title: t("draft_order_title"),
				description: "",
				isDraft: true,
			},
			{
				onSuccess: (order) => {
					toast.success(t("new_order_success"));
					navigate({
						to: "/commerce/orders/$id",
						params: { id: order.id },
					});
				},
				onError: () => toast.error(t("new_order_error")),
			},
		);
	}, [createOrderMut, t, navigate]);

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content: hasSelection
					? `${selectedRows.length} ${t("common:selected", { defaultValue: "выбрано" })}`
					: `${totalRows} ${t("total_count_label", { defaultValue: "заказов" })}`,
				align: "left" as const,
			},
		],
		[hasSelection, selectedRows.length, totalRows, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<div className="flex flex-col h-full">
			<ExportGuide />
			{/* Search + action bar */}
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						ref={searchRef}
						placeholder={t("search_placeholder", {
							defaultValue: "Поиск заказов…",
						})}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="pl-9 h-9"
					/>
				</div>
				{/* View switcher */}
				<div className="flex items-center rounded-md border border-border">
					<button
						type="button"
						onClick={() =>
							navigate({
								to: "/commerce/orders",
								search: { view: "table" },
							})
						}
						className={cn(
							"inline-flex items-center justify-center h-7 w-7 rounded-l-md transition-colors",
							viewParam === "table"
								? "bg-accent text-accent-foreground"
								: "text-muted-foreground hover:text-foreground hover:bg-accent/50",
						)}
						title={t("view_table")}
					>
						<LayoutList className="h-3.5 w-3.5" />
					</button>
					<button
						type="button"
						onClick={() =>
							navigate({
								to: "/commerce/orders",
								search: { view: "board" },
							})
						}
						className={cn(
							"inline-flex items-center justify-center h-7 w-7 rounded-r-md transition-colors",
							viewParam === "board"
								? "bg-accent text-accent-foreground"
								: "text-muted-foreground hover:text-foreground hover:bg-accent/50",
						)}
						title={t("view_board")}
					>
						<Columns3 className="h-3.5 w-3.5" />
					</button>
				</div>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() =>
						gridRef.current?.api?.exportDataAsCsv({
							fileName: "orders.csv",
						})
					}
				>
					{t("common:export", { defaultValue: "Экспорт" })}
				</Button>
				<Button size="sm" onClick={handleCreateOrder}>
					<Plus className="h-4 w-4 mr-1" />
					{t("new_order")}
				</Button>
			</div>

			{/* Grid / Board */}
			{viewParam === "board" ? (
				<KanbanBoard<SalesOrder>
					items={boardItems}
					columns={statusOptions.map((opt) => ({
						id: opt.id,
						label: opt.displayName || opt.name,
						color: opt.color || "hsl(var(--muted-foreground))",
					}))}
					getColumnId={(item) => item.statusConfigId}
					onColumnChange={(itemId, newColumnId) =>
						updateOrderStatusMut.mutate({
							id: itemId,
							newStatusConfigId: newColumnId,
						})
					}
					onItemClick={(id) =>
						navigate({
							to: "/commerce/orders/$id",
							params: { id },
						})
					}
					emptyLabel={t("kanban_empty")}
					unsetLabel={t("kanban_unset_status")}
					renderCard={(order) => (
						<>
							<div className="flex items-center gap-1.5">
								<span className="font-medium truncate leading-tight">
									{order.title || `#${order.seqNo}`}
								</span>
							</div>
							<div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-xs text-muted-foreground">
								{order.customerPartyId && (
									<span className="truncate max-w-[140px]">
										{order.customerPartyId.slice(0, 8)}
									</span>
								)}
								<span className="font-tabular">
									{formatCurrency(
										order.lines.reduce(
											(sum, l) =>
												sum +
												Number(l.quantity) *
													Number(l.unitPriceCents) *
													(1 - (l.discountPct ?? 0) / 100),
											0,
										),
									)}
								</span>
								{order.createdAt && (
									<span>
										{formatRelative(
											new Date(Number(order.createdAt.seconds) * 1000),
										)}
									</span>
								)}
							</div>
						</>
					)}
				/>
			) : (
				<div className="flex-1 ag-theme-quartz" style={{ width: "100%" }}>
					<AgGridReact<SalesOrder>
						ref={gridRef}
						{...gridProps}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						localeText={AG_GRID_LOCALE_RU}
						rowSelection="multiple"
						onSelectionChanged={onSelectionChanged}
						onRowClicked={onRowClicked}
						onGridReady={onGridReady}
						onSortChanged={() => {
							onStateChanged();
							refreshGrid();
						}}
						onFilterChanged={() => {
							onStateChanged();
							refreshGrid();
						}}
						onColumnMoved={onStateChanged}
						onColumnResized={onStateChanged}
						onColumnVisible={onStateChanged}
						suppressRowClickSelection
						rowClass="cursor-pointer"
						animateRows={false}
						rowHeight={28}
						headerHeight={32}
						getRowId={(p) => p.data.id}
						noRowsOverlayComponent={() => (
							<CommerceEmptyState
								title={t("empty_title", {
									defaultValue: "Заказов пока нет",
								})}
								description={t("empty_description", {
									defaultValue: "Создайте первый заказ",
								})}
								actionLabel={t("new_order")}
								onAction={handleCreateOrder}
							/>
						)}
					/>
				</div>
			)}
			<ActionBar />
		</div>
	);
}
